import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Globe, UserCog } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import MobileEventDialog from "@/components/admin/MobileEventDialog";
import { MOBILE_STATUSES, formatAdminDate } from "@/components/admin/adminShared";

const TH = "text-left px-5 py-4 uppercase tracking-[0.18em] text-[10px]";

function StatusSelect({ ev, onChange }) {
  return (
    <Select value={ev.status} onValueChange={(v) => onChange(ev, v)}>
      <SelectTrigger className={`h-7 w-32 rounded-full text-[11px] ${MOBILE_STATUSES[ev.status]?.chip}`} data-testid={`mobile-row-status-${ev.id}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(MOBILE_STATUSES).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function AdminMobileEvents({ editId, onEditHandled }) {
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState({ open: false, event: null });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setFetching(true);
    try { setItems((await api.get("/mobile/events")).data); }
    catch { toast.error("Error carregant esdeveniments externs."); }
    finally { setFetching(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!editId || fetching) return;
    const ev = items.find((i) => i.id === editId);
    if (ev) setDialog({ open: true, event: ev }); else toast.error("Esdeveniment no trobat.");
    onEditHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, fetching, items]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => (filter === "all" || i.status === filter) &&
      (!q || `${i.title} ${i.client_name} ${i.location} ${i.client_phone}`.toLowerCase().includes(q)));
  }, [items, filter, search]);
  const pendingCount = items.filter((i) => i.status === "pending").length;

  const changeStatus = async (ev, status) => {
    try {
      const { id, source, created_at, updated_at, ...rest } = ev;
      await api.put(`/mobile/events/${id}`, { ...rest, status });
      toast.success(`Estat: ${MOBILE_STATUSES[status].label}`);
      load();
    } catch { toast.error("No s'ha pogut canviar l'estat."); }
  };

  const onDelete = async () => {
    try { await api.delete(`/mobile/events/${confirmDelete.id}`); toast.success("Eliminat."); setConfirmDelete(null); load(); }
    catch { toast.error("No s'ha pogut eliminar."); }
  };

  return (
    <div data-testid="admin-mobile-events">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="uppercase tracking-[0.28em] text-xs text-[#C9A24B] mb-2">Gestió Esdeveniments Externs</p>
          <h2 className="font-display text-4xl tracking-tight">Esdeveniments fora del local</h2>
          <p className="text-sm text-[#5C544F] mt-1" data-testid="mobile-pending-count">{pendingCount} sol·licituds pendents</p>
        </div>
        <Button onClick={() => setDialog({ open: true, event: null })} data-testid="admin-create-mobile-event"
          className="rounded-full bg-[#3e664c] hover:bg-[#2e5949] text-[#fdfcf9] gap-2 px-6 py-5 text-sm uppercase tracking-[0.18em]">
          <Plus size={16} /> Nou esdeveniment extern
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cercar client, ubicació…" className="max-w-xs bg-white" data-testid="mobile-search" />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44 bg-white" data-testid="mobile-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tots els estats</SelectItem>
            {Object.entries(MOBILE_STATUSES).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-[#eae0df] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#fdfcf9] text-[#5C544F]">
              <tr><th className={TH}>Data</th><th className={TH}>Client / esdeveniment</th><th className={TH}>Ubicació</th><th className={TH}>Pers.</th><th className={TH}>Serveis</th><th className={TH}>Estat</th><th className={TH}>Origen</th><th className="px-5 py-4" /></tr>
            </thead>
            <tbody data-testid="admin-mobile-table">
              {fetching ? (
                <tr><td colSpan={8} className="px-5 py-10 text-center text-[#5C544F]">…</td></tr>
              ) : visible.length === 0 ? (
                <tr><td colSpan={8} className="px-5 py-10 text-center text-[#5C544F]">No hi ha esdeveniments externs.</td></tr>
              ) : visible.map((ev) => (
                <tr key={ev.id} className="border-t border-[#eae0df] align-top" data-testid={`mobile-row-${ev.id}`}>
                  <td className="px-5 py-4 whitespace-nowrap">{formatAdminDate(ev.date)}{ev.time && ` · ${ev.time}`}</td>
                  <td className="px-5 py-4"><p className="font-medium">{ev.client_name}</p><p className="text-xs text-[#5C544F]">{ev.event_type || ev.title}</p><p className="text-xs text-[#5C544F]">{ev.client_phone}</p></td>
                  <td className="px-5 py-4 max-w-[180px]">{ev.location || "—"}</td>
                  <td className="px-5 py-4">{ev.attendees ?? "—"}</td>
                  <td className="px-5 py-4 max-w-[220px] text-xs text-[#5C544F]">{ev.services?.join(", ") || "—"}</td>
                  <td className="px-5 py-4"><StatusSelect ev={ev} onChange={changeStatus} /></td>
                  <td className="px-5 py-4 text-xs text-[#5C544F]">
                    {ev.source === "web" ? <span className="inline-flex items-center gap-1"><Globe size={12} /> Web</span> : <span className="inline-flex items-center gap-1"><UserCog size={12} /> Admin</span>}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="icon" variant="ghost" onClick={() => setDialog({ open: true, event: ev })} data-testid={`mobile-edit-${ev.id}`}><Pencil size={15} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(ev)} data-testid={`mobile-delete-${ev.id}`}><Trash2 size={15} className="text-[#450905]" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <MobileEventDialog open={dialog.open} event={dialog.event} onClose={() => setDialog({ open: false, event: null })}
        onSaved={() => { setDialog({ open: false, event: null }); load(); }} />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="mobile-confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Eliminar esdeveniment extern?</AlertDialogTitle>
            <AlertDialogDescription>Aquesta acció no es pot desfer. S&apos;eliminarà {confirmDelete?.client_name}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mobile-confirm-cancel">Cancel·lar</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} data-testid="mobile-confirm-ok" className="bg-[#450905] hover:bg-[#2c0603]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
